import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Shell from './components/layout/Shell';
import Home from './pages/Home';
import Login from './pages/Login';

// Lazy loading para optimizar el bundle inicial y code-splitting
const Whiteboard = lazy(() => import('./components/tools/Whiteboard'));
const Geoboard = lazy(() => import('./components/tools/Geoboard'));
const LineClassification = lazy(() => import('./features/geometry/LineClassification'));
const Symmetry = lazy(() => import('./features/geometry/Symmetry'));
const Settings = lazy(() => import('./pages/Settings'));
const Achievements = lazy(() => import('./pages/Achievements'));
const Polygons = lazy(() => import('./features/geometry/Polygons'));
const CuerposGeometricos = lazy(() => import('./features/geometry/CuerposGeometricos'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));

const PageLoader = () => (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
        <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin" />
        <span className="font-black text-brand-purple text-base animate-pulse">Cargando aventura... 🚀</span>
    </div>
);

const Units = () => (
    <div className="p-8">
        <h2 className="text-3xl font-bold text-brand-purple mb-8">Unidades de Aprendizaje</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="1" className="bg-white p-6 rounded-3xl shadow-lg border-b-4 border-gray-200 hover:scale-105 transition-transform group cursor-pointer block">
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">📏</div>
                <h3 className="text-xl font-bold text-gray-800">Unidad 1: Líneas</h3>
                <p className="text-gray-500 text-sm mt-2">Rectas, curvas, poligonales, paralelas...</p>
                <div className="mt-4 bg-brand-blue/10 text-brand-blue font-bold py-2 px-4 rounded-xl text-center">
                    ¡Entrar! 🚀
                </div>
            </Link>

            <Link to="2" className="bg-white p-6 rounded-3xl shadow-lg border-b-4 border-gray-200 hover:scale-105 transition-transform group cursor-pointer block">
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">🔷</div>
                <h3 className="text-xl font-bold text-gray-800">Unidad 2: Polígonos</h3>
                <p className="text-gray-500 text-sm mt-2">Lados, vértices y nombres de las figuras.</p>
                <div className="mt-4 bg-brand-orange/10 text-brand-orange font-bold py-2 px-4 rounded-xl text-center">
                    ¡Entrar! 🚀
                </div>
            </Link>

            <Link to="3" className="bg-white p-6 rounded-3xl shadow-lg border-b-4 border-gray-200 hover:scale-105 transition-transform group cursor-pointer block">
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">🔶</div>
                <h3 className="text-xl font-bold text-gray-800">Unidad 3: Cuerpos Geométricos</h3>
                <p className="text-gray-500 text-sm mt-2">Prismas, pirámides, cilindros, conos y esferas.</p>
                <div className="mt-4 bg-brand-green/10 text-brand-green font-bold py-2 px-4 rounded-xl text-center">
                    ¡Entrar! 🚀
                </div>
            </Link>

            <Link to="1/symmetry" className="bg-white p-6 rounded-3xl shadow-lg border-b-4 border-gray-200 hover:scale-105 transition-transform group cursor-pointer block">
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">🦋</div>
                <h3 className="text-xl font-bold text-gray-800">Taller: Simetría</h3>
                <p className="text-gray-500 text-sm mt-2">Traza ejes y dibuja figuras en espejo.</p>
                <div className="mt-4 bg-purple-50 text-brand-purple font-bold py-2 px-4 rounded-xl text-center">
                    ¡Entrar! 🚀
                </div>
            </Link>
        </div>
    </div>
);

const AppRoutes = () => {
    const { user } = useAuth();

    return (
        <Routes>
            {!user ? (
                <Route path="*" element={<Login />} />
            ) : (
                <Route path="/" element={<Shell />}>
                    <Route index element={<Home />} />
                    <Route path="units" element={<Units />} />
                    <Route path="units/1" element={
                        <Suspense fallback={<PageLoader />}>
                            <LineClassification />
                        </Suspense>
                    } />
                    <Route path="units/2" element={
                        <Suspense fallback={<PageLoader />}>
                            <Polygons />
                        </Suspense>
                    } />
                    <Route path="units/3" element={
                        <Suspense fallback={<PageLoader />}>
                            <CuerposGeometricos />
                        </Suspense>
                    } />
                    <Route path="units/1/symmetry" element={
                        <Suspense fallback={<PageLoader />}>
                            <Symmetry />
                        </Suspense>
                    } />
                    <Route path="whiteboard" element={
                        <Suspense fallback={<PageLoader />}>
                            <Whiteboard />
                        </Suspense>
                    } />
                    <Route path="geoboard" element={
                        <Suspense fallback={<PageLoader />}>
                            <Geoboard />
                        </Suspense>
                    } />
                    <Route path="achievements" element={
                        <Suspense fallback={<PageLoader />}>
                            <Achievements />
                        </Suspense>
                    } />
                    <Route path="teacher" element={
                        <Suspense fallback={<PageLoader />}>
                            <TeacherDashboard />
                        </Suspense>
                    } />
                    <Route path="settings" element={
                        <Suspense fallback={<PageLoader />}>
                            <Settings />
                        </Suspense>
                    } />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
            )}
        </Routes>
    );
};

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;

